/**
 * JAIP Widget loader. Tiny vanilla-JS bootstrapper die op elke pageload draait.
 * Bouwt een floating button in een Shadow DOM en lazy-loadt `widget.js` pas
 * bij de eerste klik. Zo blijft de baseline-pageload-cost minimaal.
 *
 * Gebruik:
 *   <script
 *     src="https://widget.jouw-ai-partner.nl/loader.js"
 *     data-project="<project-uuid>"
 *     data-user-email="optional@example.com"
 *     async
 *   ></script>
 */

// Forceert dat TypeScript dit bestand als module behandelt (anders is
// `declare global` niet toegestaan). esbuild bundelt 'm sowieso als IIFE,
// dus het lege export is runtime een no-op.
export {};

interface MountConfig {
  projectId: string;
  apiUrl: string;
  userEmail: string | null;
  /**
   * WG-006: URL van widget.js zodat de modal er widget-screenshot.js uit
   * kan afleiden voor lazy-loading van de capture-bundle. Loader weet 'm
   * al voor de eigen script-tag-injectie; doorgeven is goedkoop.
   */
  bundleSrc: string;
}

interface JAIPWidgetGlobal {
  mount: (root: ShadowRoot, config: MountConfig) => void;
}

interface JAIPWidgetIdentifyInfo {
  email: string;
}

declare global {
  interface Window {
    __JAIPWidget?: JAIPWidgetGlobal;
    /**
     * Runtime-API voor SPA's die het ingelogde email pas na hydration weten
     * (bijv. na een `/me`-fetch). Volgende keer dat de modal mountt gebruikt
     * hij de nieuwe email. WG-004 (klant-rollout).
     */
    __JAIPWidgetIdentify?: (info: JAIPWidgetIdentifyInfo) => void;
  }
}

(function () {
  const script = document.currentScript as HTMLScriptElement | null;
  if (!script) {
    console.warn("[JAIP Widget] geen currentScript context — loader genegeerd");
    return;
  }
  const projectId = script.dataset.project;
  if (!projectId) {
    console.warn("[JAIP Widget] data-project attribuut ontbreekt — loader genegeerd");
    return;
  }

  // API-URL hardcoded naar productie. Voor staging override via een global
  // hook (window.__JAIPWidgetApiUrl) — geen data-attribute zodat klanten
  // niet per ongeluk hun eigen DevHub kiezen.
  const apiUrl =
    (window as unknown as { __JAIPWidgetApiUrl?: string }).__JAIPWidgetApiUrl ??
    "https://devhub.jouw-ai-partner.nl/api/ingest/widget";

  // Een host die `data-user-email=""` zet (SPA-form-veld nog niet ingevuld)
  // zou anders een lege string sturen waarop Zod `.email()` server-side
  // faalt — terwijl reporter_email optioneel is. Server blijft de strikte
  // check; client weert alleen het meest voorkomende geval.
  function normalizeEmail(value: string | null | undefined): string | null {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    if (trimmed.length === 0 || trimmed.length > 320) return null;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return null;
    return trimmed;
  }

  let userEmail = normalizeEmail(script.dataset.userEmail);

  // Runtime-identify voor klant-SPA's. Triage gebruikt dit email als hint,
  // niet als bewijs — een browser kan altijd spoofen, maar de origin-
  // whitelist blijft als hek staan. Een ongeldige call laat de bestaande
  // waarde staan i.p.v. 'm te wissen.
  window.__JAIPWidgetIdentify = (info) => {
    const next = normalizeEmail(info?.email);
    if (next) userEmail = next;
  };

  const host = document.createElement("div");
  host.id = "__jaip-widget-host";
  host.style.cssText = "position:fixed;bottom:0;right:0;z-index:2147483647;";
  document.body.appendChild(host);
  const shadow = host.attachShadow({ mode: "open" });

  const button = document.createElement("button");
  button.type = "button";
  button.textContent = "Feedback";
  button.setAttribute("aria-label", "Feedback geven");
  // Verticale side-tab op de rechterrand. `writing-mode: vertical-rl` draait
  // de letters zodat ze van boven naar beneden gelezen worden; alleen de
  // linkerhoeken afgerond zodat de knop visueel aan de rand vastzit i.p.v.
  // boven de content te zweven.
  button.style.cssText = [
    "position:fixed",
    "top:50%",
    "right:0",
    "transform:translateY(-50%)",
    "writing-mode:vertical-rl",
    "padding:18px 8px",
    "border-radius:12px 0 0 12px",
    "background:#0f172a",
    "color:#fff",
    "border:0",
    "cursor:pointer",
    "font:500 13px system-ui",
    "letter-spacing:0.04em",
    "box-shadow:-2px 4px 12px rgba(0,0,0,0.15)",
  ].join(";");
  shadow.appendChild(button);

  // In-flight load wordt als gedeelde Promise opgeslagen zodat concurrente
  // klikken (dubbelklik tijdens load) dezelfde tag-injection delen — geen
  // setTimeout-polling die in een oneindige loop kan blijven hangen als
  // widget.js nooit laadt.
  let widgetLoadPromise: Promise<void> | null = null;

  // Cross-origin script injection ipv dynamic import: robuuster en werkt
  // ook in oudere browsers zonder een module-bundler-runtime aan kant van
  // de host.
  const widgetSrc = script!.src.replace(/loader\.js(\?.*)?$/, "widget.js");

  function loadWidget(): Promise<void> {
    if (widgetLoadPromise) return widgetLoadPromise;

    widgetLoadPromise = new Promise<void>((resolve, reject) => {
      const tag = document.createElement("script");
      tag.src = widgetSrc;
      tag.async = true;
      tag.onload = () => resolve();
      tag.onerror = () => {
        // Reset zodat een volgende klik opnieuw mag proberen — anders
        // blijft de widget dood na één netwerk-glitch.
        widgetLoadPromise = null;
        reject(new Error("widget.js failed to load"));
      };
      document.head.appendChild(tag);
    });

    return widgetLoadPromise;
  }

  button.addEventListener("click", async () => {
    try {
      await loadWidget();
      window.__JAIPWidget?.mount(shadow, {
        projectId,
        apiUrl,
        userEmail,
        bundleSrc: widgetSrc,
      });
    } catch (err) {
      console.error("[JAIP Widget]", err);
    }
  });
})();
